/*
 * What the reply box offers while it holds the reply agent's draft, on the unified page for a person
 * with the agent (Dawid's pickers: ledger `87f82a84`, hints and ending in the box; `72427ecc`, the
 * pending-answer warning). The draft's hints panel sits under the editor, "Change ending" picks one of
 * the saved sign-offs from the box's toolbar, and a line says when the agent still waits on the rep's
 * answer. With the rep's own words in the box, only the hints show: the rest is about the agent's draft.
 *
 * The ending is saved with the box's words in one draft request (`agent/approve-from-box.ts`
 * `saveEnding`), never two writes, and the box stays frozen while it saves, as during an approval.
 */
import { useMemo, useState } from "react";
import type { SignOffMode, SignOffPreviews } from "../../src/sign-off-mode.ts";
import {
  type ClosingCheck,
  type DraftHintSignOff,
  type DraftHints,
  DraftHintsPanel,
  draftHints,
  useDraftHints,
} from "../draft-hints.tsx";
import { Segmented } from "../drafting-switches.tsx";
import type { EditorLinks } from "../editor-links.tsx";
import type { BoxContent } from "./reply-box-link.ts";

/** The reply agent's offer to the box beside its draft (`agent/draft-aids.ts`). */
export type DraftAids = {
  /** How the draft ends and the saved endings it may take; null where its ending is in its body. */
  readonly ending: {
    readonly mode: SignOffMode;
    readonly plainText: string;
    readonly options: SignOffPreviews | null;
  } | null;
  /** The first question the agent waits on, and how many more; null once all are answered. */
  readonly waitingOn: { readonly first: string; readonly more: number } | null;
  /** Whether the stored ending still closes the box's words, so another saved one can replace it. */
  readonly endingIntact: (box: BoxContent) => boolean;
  /**
   * Saves the box's words with another saved ending in one request; the saved revision reaches the
   * box this conversation has open through its link, even one opened again while the save ran.
   */
  readonly changeEnding: (mode: SignOffMode) => Promise<void>;
};

const ENDINGS = (options: SignOffPreviews | null) =>
  [
    { value: "first_name", label: "Best, first name" },
    { value: "none", label: "None" },
    {
      value: "full_signature",
      label: "Full signature",
      unavailable: options !== null && options.full_signature === null,
    },
  ] as const;

/**
 * The pending-answer line: the draft is written before the rep's answer and may be redrafted. The
 * question is quoted, so its own question mark never runs into "and 1 more".
 */
export function WaitingLine(props: { waitingOn: NonNullable<DraftAids["waitingOn"]> }) {
  const { first, more } = props.waitingOn;
  return (
    <div className="lcr-reply-notice lcr-i2-waiting" data-tone="warn" role="status">
      <p>
        The agent is waiting on your answer to “{first.trim()}”{more > 0 ? ` and ${more} more` : ""}
        . This draft may change once you answer.
      </p>
    </div>
  );
}

/**
 * "Change ending" in the box's toolbar, and the saved endings it opens onto. Its error outlives the
 * box holding the draft (a refusal can follow a newer draft arriving) and goes when the box takes
 * another revision.
 */
export function useEndingChoice(aids: DraftAids | null, frozen: boolean, loaded: number | null) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  // Another revision in the box resets the choice, during render rather than after a paint.
  const [shown, setShown] = useState(loaded);
  if (shown !== loaded) {
    setShown(loaded);
    setOpen(false);
    setError("");
  }
  return {
    open: open && aids !== null && aids.ending !== null,
    error,
    toggle: () => {
      setOpen(!open);
      setError("");
    },
    choose: async (mode: SignOffMode) => {
      if (!aids || frozen) return;
      setError("");
      try {
        await aids.changeEnding(mode);
        setOpen(false);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "The ending did not change.");
      }
    },
  };
}
export type EndingChoice = ReturnType<typeof useEndingChoice>;

export function EndingButton(props: { choice: EndingChoice; disabled: boolean; controls: string }) {
  return (
    <button
      type="button"
      className="lcr-small-button lcr-ghost lcr-i2-ending-toggle"
      aria-expanded={props.choice.open}
      aria-controls={props.choice.open ? props.controls : undefined}
      disabled={props.disabled}
      onClick={props.choice.toggle}
    >
      {props.choice.open ? "Keep this ending" : "Change ending"}
    </button>
  );
}

export function EndingChoices(props: {
  id: string;
  ending: NonNullable<DraftAids["ending"]>;
  frozen: boolean;
  onChoose: (mode: SignOffMode) => void;
}) {
  return (
    <div className="lcr-ending-choices lcr-i2-ending" id={props.id}>
      <Segmented
        label="How this draft ends"
        value={props.ending.mode}
        options={ENDINGS(props.ending.options)}
        disabled={props.frozen}
        onChange={props.onChoose}
      />
      <p className="lcr-drafting-note">
        This draft only. The campaign keeps its own setting, and your edits in the box stay as they
        are.
      </p>
    </div>
  );
}

/** What the box's hints share in either format: how the reply closes and its Add signature. */
type ClosingProps = {
  closing: ClosingCheck;
  onAddSignature: (() => void) | undefined;
};

/** The reply's hints, read from the editor's own document in Formatted. */
function FormattedHints(
  props: ClosingProps & {
    editor: DraftHints["editor"];
    signOff: DraftHintSignOff;
    links: EditorLinks;
    id: string;
    frozen: boolean;
  },
) {
  const hints = useDraftHints(props.editor, props.signOff, { closing: props.closing });
  return (
    <DraftHintsPanel
      hints={hints}
      links={props.links}
      id={props.id}
      frozen={props.frozen}
      verb="send"
      {...(props.onAddSignature ? { onAddSignature: props.onAddSignature } : {})}
    />
  );
}

/** In Plain text there are no links to check: typed markup, an empty reply, an empty ending. */
function PlainHints(
  props: ClosingProps & {
    text: string;
    signOff: DraftHintSignOff;
    links: EditorLinks;
    id: string;
    frozen: boolean;
  },
) {
  const { text, signOff, closing } = props;
  const hints = useMemo(
    () =>
      draftHints({
        links: [],
        texts: [{ text, from: 0 }],
        signOff,
        closing: { text, plain: true, check: closing },
      }),
    [text, signOff, closing],
  );
  const state = useMemo(() => ({ editor: null, hints, recheck: () => undefined }), [hints]);
  return (
    <DraftHintsPanel
      hints={state}
      links={props.links}
      id={props.id}
      frozen={props.frozen}
      verb="send"
      {...(props.onAddSignature ? { onAddSignature: props.onAddSignature } : {})}
    />
  );
}

/**
 * The hints panel under the box's editor, as the reply agent's own editor draws it, for the agent's
 * draft and the rep's own words alike. A frozen box (an approval or a change of ending in flight)
 * offers no fix: the saved revision would undo it.
 */
export function BoxHints(props: {
  format: "text" | "html";
  editor: DraftHints["editor"];
  text: string;
  ending: DraftAids["ending"];
  /** Who the reply goes from, so the hints can say it is unsigned or signed by someone else. */
  signature: string | null;
  sender: string | null;
  onAddSignature: (() => void) | undefined;
  links: EditorLinks;
  id: string;
  frozen: boolean;
}) {
  const { ending, signature, sender } = props;
  const signOff = useMemo<DraftHintSignOff>(
    () => (ending ? { mode: ending.mode, plainText: ending.plainText } : null),
    [ending],
  );
  const closing = useMemo<ClosingCheck>(() => ({ signature, sender }), [signature, sender]);
  return props.format === "html" ? (
    <FormattedHints
      editor={props.editor}
      signOff={signOff}
      closing={closing}
      onAddSignature={props.onAddSignature}
      links={props.links}
      id={props.id}
      frozen={props.frozen}
    />
  ) : (
    <PlainHints
      text={props.text}
      signOff={signOff}
      closing={closing}
      onAddSignature={props.onAddSignature}
      links={props.links}
      id={props.id}
      frozen={props.frozen}
    />
  );
}
